import React, { useState } from 'react';
import { X, Loader2, ExternalLink, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../services/api/client';
import { ENDPOINTS } from '../../../services/api/endpoints';

const COUNTRY_OPTIONS = [
  { id: 'IN', label: 'India (IN)' },
  { id: 'US', label: 'United States (US)' },
  { id: 'GB', label: 'United Kingdom (GB)' },
  { id: 'DE', label: 'Germany (DE)' },
  { id: 'AE', label: 'United Arab Emirates (AE)' },
];

/**
 * Modal that kicks off the Amazon Advertising API OAuth (LWA consent) flow for a specific seller
 * account. Mirrors the SP-API AmazonConnectModal: opens the authorization URL in a new tab and waits
 * for the AdsOAuthCallback page to signal completion via the `ads_oauth_complete` localStorage key.
 */
export default function AdsConnectModal({ isOpen, onClose, onConnect, sellerAccountId }) {
  const [countryCode, setCountryCode] = useState('IN');
  const [authorizing, setAuthorizing] = useState(false);

  if (!isOpen) return null;

  const handleOAuthAuthorize = async () => {
    if (!sellerAccountId) {
      toast.error('Select a marketplace account first');
      return;
    }
    setAuthorizing(true);
    try {
      const res = await apiClient.get(
        `${ENDPOINTS.AD_PERFORMANCE.OAUTH_AUTHORIZE}?sellerAccountId=${sellerAccountId}&countryCode=${countryCode}`
      );
      const data = res.data || res;
      if (data.authorizationUrl) {
        window.open(data.authorizationUrl, '_blank');
        const handleStorage = (e) => {
          if (e.key === 'ads_oauth_complete') {
            window.removeEventListener('storage', handleStorage);
            localStorage.removeItem('ads_oauth_complete');
            setAuthorizing(false);
            onClose();
            if (typeof onConnect === 'function') onConnect({ _oauthComplete: true });
            toast.success('Amazon Ads account connected!');
          }
        };
        window.addEventListener('storage', handleStorage);
        setTimeout(() => {
          window.removeEventListener('storage', handleStorage);
          setAuthorizing(false);
        }, 300000);
      } else {
        toast.error(data.error || 'Failed to generate authorization URL');
        setAuthorizing(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to start authorization');
      setAuthorizing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#FFF4E6] to-[#FFE8C8]/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#f08804] flex items-center justify-center">
              <Megaphone size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Connect Amazon Ads</h3>
              <p className="text-xs text-gray-500">Link your Sponsored Ads account</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-xs text-gray-500">
            The Amazon Advertising API is a separate authorization from your Seller (SP-API) connection.
            Authorize it once to pull campaign spend, sales &amp; ACOS.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Country / Region</label>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="bg-[#FFF9EE] border border-[#FFE8C8] rounded-xl p-5">
            {authorizing ? (
              <div className="text-center py-3">
                <Loader2 size={22} className="animate-spin text-[#C45500] mx-auto mb-3" />
                <p className="text-base font-semibold text-[#C45500]">Almost there!</p>
                <p className="text-sm text-amber-700 mt-1.5 leading-relaxed">
                  We&apos;ve opened Amazon Ads in a new tab.<br />
                  Log in and authorize eComm Notify.
                </p>
                <p className="text-xs text-amber-500 mt-3">We&apos;ll connect automatically once done.</p>
                <button onClick={() => setAuthorizing(false)} className="text-xs text-amber-500 hover:text-[#C45500] mt-3 underline">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleOAuthAuthorize}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] transition-colors shadow-sm"
              >
                <ExternalLink size={16} />
                Authorize with Amazon Ads
              </button>
            )}
          </div>

          <div className="flex justify-end">
            <button onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
